import { Box, Container, useMediaQuery, useTheme } from '@mui/material';
import { Suspense, useEffect, useMemo, useState } from 'react';

import { InterruptedCashinHost } from '@features/cashier/pages/cashin/InterruptedCashinHost';
import NestedRouting from '@routes/NestedRouting';
import { LazyUserDetails } from '@routes/lazyComponents';
import { ReportProblemButton } from '@shared/components/ReportProblem/ReportProblemButton';
import { PageLayoutContext, useCurrentUser } from '@shared/hooks';
import AppBarHeader from './AppBarHeader';
import DrawerMenu from './DrawerMenu';

import './Dashboard.scss';

const drawerWidth = 270;
const SIDEBAR_BREAKPOINT = 1200;

const getContainerMaxWidth = (isXL, isLG) => {
  if (isXL) return 'xl';
  if (isLG) return 'lg';
  return 'md';
};

const DashboardLayout = () => {
  const theme = useTheme();
  const user = useCurrentUser();
  const { change_password: changePassword } = user;

  const isWideScreen = useMediaQuery(`(min-width:${SIDEBAR_BREAKPOINT}px)`);
  const [mobileOpen, setMobileOpen] = useState(isWideScreen);

  useEffect(() => {
    setMobileOpen(isWideScreen);
  }, [isWideScreen]);

  const isXL = useMediaQuery(theme.breakpoints.up('xl'));
  const isLG = useMediaQuery(theme.breakpoints.up('lg'));
  const maxWidth = getContainerMaxWidth(isXL, isLG);

  const [wide, setWide] = useState(false);
  const pageLayout = useMemo(() => ({ setWide }), []);
  const effectiveMaxWidth = wide ? false : maxWidth;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleClose = () => {
    if (window.innerWidth < 960) {
      setMobileOpen(false);
    }
  };

  const contentOffset = mobileOpen ? `${drawerWidth}px` : theme.spacing(9);

  return (
    <Box className="principal-container">
      <InterruptedCashinHost />
      <ReportProblemButton />
      <AppBarHeader onDrawerToggle={handleDrawerToggle} mobileOpen={mobileOpen} />
      {!changePassword && (
        <DrawerMenu mobileOpen={mobileOpen} onDrawerToggle={handleDrawerToggle} handleClose={handleClose} />
      )}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box
          component="main"
          className="content"
          sx={{
            flexGrow: 1,
            marginLeft: 'var(--content-left)',
            '--content-left': '0px',
            transition: theme.transitions.create(['margin'], {
              duration: theme.transitions.duration.standard,
            }),
            [theme.breakpoints.up('sm')]: {
              '--content-left': contentOffset,
            },
          }}>
          <Container
            maxWidth={effectiveMaxWidth}
            disableGutters={wide}
            sx={{
              paddingTop: theme.spacing(1.5),
              paddingBottom: `calc(${theme.spacing(4)} + env(safe-area-inset-bottom, 0px))`,
              ...(wide && { px: { xs: 2, md: 3 } }),
              transition: theme.transitions.create(['max-width', 'padding'], {
                duration: theme.transitions.duration.standard,
              }),
            }}>
            <PageLayoutContext.Provider value={pageLayout}>
              {changePassword ? (
                <Suspense
                  fallback={
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                      <span>Cargando...</span>
                    </div>
                  }>
                  <LazyUserDetails changePassword />
                </Suspense>
              ) : (
                <NestedRouting />
              )}
            </PageLayoutContext.Provider>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
